import React, { useEffect } from 'react'
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  Button,
} from '@t-one-internal/design-system'
import { ArrowLeft, ArrowRight, PaperPlaneTilt } from '@phosphor-icons/react'
import { useGiveRecognitionForm } from './useGiveRecognitionForm'
import RecipientStep from './RecipientStep'
import TypeStep from './TypeStep'
import MessageStep from './MessageStep'
import ValuePreviewStep from './ValuePreviewStep'
import SuccessOverlay from './SuccessOverlay'

const STEPS = ['Type', 'Recipient', 'Message', 'Value & Preview']

interface GiveRecognitionDialogProps {
  open: boolean
  onOpenChange: (open: boolean) => void
}

const GiveRecognitionDialog: React.FC<GiveRecognitionDialogProps> = ({ open, onOpenChange }) => {
  const form = useGiveRecognitionForm(() => onOpenChange(false))

  useEffect(() => {
    if (open) {
      // Employees load when type is selected (handleTypeChange)
    }
  }, [open])

  const handleOpenChange = (nextOpen: boolean) => {
    if (!nextOpen) {
      form.resetForm()
    }
    onOpenChange(nextOpen)
  }

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent className="sm:max-w-[560px] p-0 gap-0">
        {/* Success overlay */}
        {form.showSuccess && <SuccessOverlay />}

        {/* Header */}
        <DialogHeader className="p-5 pb-0">
          <DialogTitle className="text-lg font-bold">Give Recognition</DialogTitle>
          <p className="text-xs text-muted-foreground">Step {form.step} of 4</p>
        </DialogHeader>

        {/* Progress bar */}
        <div className="flex gap-1 px-5 mt-3">
          {STEPS.map((_, i) => (
            <div
              key={i}
              className="flex-1 h-1 rounded-full transition-colors"
              style={{
                backgroundColor: i < form.step ? 'hsl(var(--primary))' : 'hsl(var(--border))',
              }}
            />
          ))}
        </div>

        {/* Step Content */}
        <div className="p-5 min-h-[340px]">
          {form.step === 1 && (
            <TypeStep
              selectedType={form.selectedType}
              onSelect={form.handleTypeChange}
              availableTypes={form.availableTypes}
            />
          )}

          {form.step === 2 && (
            <RecipientStep
              search={form.search}
              onSearchChange={form.setSearch}
              employees={form.filteredEmployees}
              selectedRecipients={form.selectedRecipients}
              onToggle={form.toggleRecipient}
              onRemove={form.removeRecipient}
              onSelectAllVisible={form.selectAllVisible}
              activeFilters={form.activeFilters}
              onFiltersChange={form.setActiveFilters}
              filterFields={form.filterFields}
              totalActiveFilters={form.totalActiveFilters}
              minRecipients={form.minRecipients}
            />
          )}

          {form.step === 3 && (
            <MessageStep
              message={form.message}
              onMessageChange={form.setMessage}
            />
          )}

          {form.step === 4 && (
            <ValuePreviewStep
              selectedValue={form.selectedValue}
              onSelectValue={form.setSelectedValue}
              selectedRecipients={form.selectedRecipients}
              selectedType={form.selectedType}
              message={form.message}
              companyValueOptions={form.companyValueOptions}
              onValueCreated={form.handleValueCreated}
              onValueDeleted={form.handleValueDeleted}
            />
          )}
        </div>

        {/* Footer navigation */}
        <div className="flex justify-between items-center p-5 pt-0">
          <Button
            variant="ghost"
            size="sm"
            onClick={form.goBack}
            className={form.step === 1 ? 'invisible' : ''}
          >
            <ArrowLeft size={16} /> Back
          </Button>

          {form.step < 4 ? (
            <Button
              size="sm"
              onClick={form.goNext}
              disabled={!form.canProceed()}
            >
              Continue <ArrowRight size={16} />
            </Button>
          ) : (
            <Button
              size="sm"
              onClick={form.handleSend}
              disabled={form.isSubmitting}
              className="gap-1.5"
            >
              <PaperPlaneTilt size={16} weight="fill" />
              Send Recognition
            </Button>
          )}
        </div>
      </DialogContent>
    </Dialog>
  )
}

export default GiveRecognitionDialog
